import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import ImageUpload from '../../../components/ImageUpload';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [durations, setDurations] = useState([{ duration: '' }]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_icebox_service: false,
    status: true,
  });

  useEffect(() => {
    if (id) getSingleData();
  }, [id]);

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/services/${id}`,
      });
      const data = response.data;
      if (data.status) {
        const service = data.data;
        setFormData({
          title: service.title || '',
          description: service.description || '',
          is_icebox_service: !!service.is_icebox_service,
          status: !!service.status,
        });
        setPreviewUrl(service.image || null);
        setDurations(
          (service.duration_options || []).length
            ? service.duration_options.map(o => ({ duration: o.duration ?? '' }))
            : [{ duration: '' }]
        );
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDurationChange = (index, field, value) => {
    setDurations((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addDurationRow = () => setDurations((prev) => [...prev, { duration: '' }]);

  const removeDurationRow = (index) => {
    setDurations((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleImageChange = (name, file) => {
    setImageFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    const cleanDurations = durations
      .filter((row) => String(row.duration).trim() !== '')
      .map((row) => ({ duration: String(row.duration).trim() }));

    if (!cleanDurations.length) {
      toast.error('Add at least one duration option');
      return;
    }

    setIsLoader(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('is_icebox_service', formData.is_icebox_service);
      data.append('status', formData.status);
      data.append('duration_options', JSON.stringify(cleanDurations));
      if (imageFile) data.append('image', imageFile);

      const response = await ApiService.request({
        method: 'POST',
        url: `admin/services/createOrUpdate/${id || ''}`,
        data,
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/admin/services');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit Service' : 'Add Service'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/services" className="text-muted text-hover-primary">Services</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body py-6">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-6">
                    <label className="required form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      placeholder="e.g. Contrast Therapy"
                    />
                  </div>

                  <div className="col-md-6 mb-6">
                    <label className="form-label">Service Image</label>
                    <ImageUpload
                      name="image"
                      onImageChange={handleImageChange}
                      defaultPreviewUrl={previewUrl}
                      maxSizeMB={5}
                    />
                  </div>

                  <div className="col-md-12 mb-6">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className="form-control form-control-solid"
                      rows="3"
                      placeholder="What the session involves"
                    />
                  </div>

                  <div className="col-md-12 mb-6">
                    <label className="required form-label">Duration Options</label>
                    <div className="form-text mb-3">
                      Each option is a bookable duration in minutes. The booking records which one the customer took.
                    </div>

                    {durations.map((row, index) => (
                      <div className="row align-items-center mb-3" key={index}>
                        <div className="col-md-4">
                          <input
                            type="number"
                            min="1"
                            value={row.duration}
                            onChange={(e) => handleDurationChange(index, 'duration', e.target.value)}
                            className="form-control form-control-solid"
                            placeholder="Duration in mins e.g. 30"
                          />
                        </div>
                        <div className="col-md-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-light-danger"
                            onClick={() => removeDurationRow(index)}
                            disabled={durations.length === 1}
                          >
                            <i className="bi bi-trash-fill fs-6"></i>
                          </button>
                        </div>
                      </div>
                    ))}

                    <button type="button" className="btn btn-sm btn-light-primary" onClick={addDurationRow}>
                      <i className="bi bi-plus-lg me-1"></i> Add Duration
                    </button>
                  </div>

                  <div className="col-md-6 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="is_icebox_service"
                        checked={formData.is_icebox_service}
                        onChange={handleChange}
                        id="is_icebox_service"
                      />
                      <label className="form-check-label" htmlFor="is_icebox_service">
                        Icebox service
                      </label>
                    </div>
                  </div>

                  <div className="col-md-6 mb-6">
                    <div className="form-check form-switch form-check-custom form-check-solid">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleChange}
                        id="status"
                      />
                      <label className="form-check-label" htmlFor="status">
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3">
                  <Link to="/admin/services" className="btn btn-light">Cancel</Link>
                  <button type="submit" className="btn btn-base" disabled={isLoader}>
                    {isLoader ? <Loader text="Saving" size="20" /> : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdate;
